import type { PrV2ReviewResponse } from '@/lib/pr-v2/types';
import type { ReviewerStateValues } from '@/types/github';

import { ReviewerState } from '@/types/github';

export type CanonicalReviewer = {
  login: string;
  avatarUrl: string | null;
  state: ReviewerStateValues;
  reviewId: string | null;
  submittedAt: string | null;
};

type RequestedReviewer = {
  login: string;
  avatarUrl: string | null;
};

type SubmittedReview = Pick<
  PrV2ReviewResponse,
  'githubReviewId' | 'state' | 'submittedAt' | 'userLogin' | 'userAvatarUrl'
>;

function normalizeReviewerState(state: string | null | undefined): ReviewerStateValues {
  const normalized = state?.trim().toUpperCase();

  switch (normalized) {
    case ReviewerState.APPROVED:
      return ReviewerState.APPROVED;
    case 'REQUEST_CHANGES':
    case ReviewerState.CHANGES_REQUESTED:
      return ReviewerState.CHANGES_REQUESTED;
    case ReviewerState.DISMISSED:
      return ReviewerState.DISMISSED;
    case ReviewerState.COMMENTED:
      return ReviewerState.COMMENTED;
    default:
      return ReviewerState.REQUESTED_REVIEW;
  }
}

function isDecisiveReviewerState(state: ReviewerStateValues): boolean {
  return (
    state === ReviewerState.APPROVED ||
    state === ReviewerState.CHANGES_REQUESTED ||
    state === ReviewerState.DISMISSED
  );
}

function toReviewTimestamp(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }

  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function toReviewId(
  githubReviewId: string | null | undefined,
  fallback: string | null,
): string | null {
  const numeric = Number(githubReviewId);
  return Number.isFinite(numeric) && numeric > 0 ? String(githubReviewId) : fallback;
}

/**
 * Builds one reviewer entry per login, keyed case-insensitively, with requested reviewers pinned
 * as REQUESTED_REVIEW.
 *
 * A login in requested_reviewers is authoritatively pending: a re-request re-adds the user there
 * (and GitHub drops them once they submit), so their pending state wins over any earlier review.
 * Reviews from non-requested reviewers collapse to their latest state, except a later COMMENTED
 * never downgrades a decisive (approved/changes-requested/dismissed) one. Login matching is
 * case-insensitive because optimistic cache updates lowercase logins.
 *
 * Shared by the authenticated sidebar and the merge-dropdown adapter so those two surfaces can't
 * drift apart.
 */
export function buildPrV2Reviewers(
  requestedReviewers: ReadonlyArray<RequestedReviewer>,
  reviews: ReadonlyArray<SubmittedReview>,
): CanonicalReviewer[] {
  const reviewerMap = new Map<string, CanonicalReviewer>();
  const requestedReviewerLogins = new Set(
    requestedReviewers.map((reviewer) => reviewer.login.toLowerCase()),
  );

  requestedReviewers.forEach((reviewer) => {
    reviewerMap.set(reviewer.login.toLowerCase(), {
      login: reviewer.login,
      avatarUrl: reviewer.avatarUrl,
      state: ReviewerState.REQUESTED_REVIEW,
      reviewId: null,
      submittedAt: null,
    });
  });

  const isReviewFromNonRequestedReviewer = (
    review: SubmittedReview,
  ): review is SubmittedReview & { userLogin: string } => {
    const login = review.userLogin;
    return login != null && login.length > 0 && !requestedReviewerLogins.has(login.toLowerCase());
  };

  const applicableReviews = reviews
    .filter(isReviewFromNonRequestedReviewer)
    .sort((a, b) => toReviewTimestamp(a.submittedAt) - toReviewTimestamp(b.submittedAt));

  applicableReviews.forEach((review) => {
    const key = review.userLogin.toLowerCase();
    const existing = reviewerMap.get(key);
    const nextState = normalizeReviewerState(review.state);

    // A later comment can't replace a decision already on record.
    if (
      nextState === ReviewerState.COMMENTED &&
      existing &&
      isDecisiveReviewerState(existing.state)
    ) {
      return;
    }

    reviewerMap.set(key, {
      login: review.userLogin,
      avatarUrl: review.userAvatarUrl ?? existing?.avatarUrl ?? null,
      state: nextState,
      reviewId: toReviewId(review.githubReviewId, existing?.reviewId ?? null),
      submittedAt: review.submittedAt ?? existing?.submittedAt ?? null,
    });
  });

  return Array.from(reviewerMap.values());
}

/** Where a pull request's reviews leave it, as GitHub's review decision; null when none decides. */
export type PrV2ReviewDecision = 'approved' | 'changes_requested';

/**
 * Changes requested while any reviewer's latest decision asks for them, else approved once one
 * approves. A re-request doesn't clear a reviewer's decision on GitHub, so only recorded reviews
 * count.
 */
export function getPrV2ReviewDecision(
  reviews: ReadonlyArray<SubmittedReview>,
): PrV2ReviewDecision | null {
  const states = new Set(buildPrV2Reviewers([], reviews).map((reviewer) => reviewer.state));
  if (states.has(ReviewerState.CHANGES_REQUESTED)) {
    return 'changes_requested';
  }
  return states.has(ReviewerState.APPROVED) ? 'approved' : null;
}
